import { useEffect, useState } from 'react';
import { sendMessage } from '../services/chat/chatService';
import { logEvent } from '../services/analytics/eventLogger';
import { ensureSignedIn } from '../services/supabase';

/**
 * useGuestSaveRetry — the guest's own writes that didn't land
 * (guest-session-loss): said on screen with Try again, never dropped silently.
 * Extracted from AnonConnectionPage (signup E3a) so the new guest screens show
 * the same card from the same rail.
 *
 * Listens for chatService's `guestSaveFailed` / `guestSaveLanded` window events
 * for this room — one entry per message id; a write that lands after all comes
 * off the list, and the card clears itself when nothing is left.
 *
 * @param {string} chatRoomId
 * @returns {{unsaved: Object[], retrying: boolean, retryUnsaved: () => Promise<void>}}
 */
const useGuestSaveRetry = (chatRoomId) => {
  const [unsaved, setUnsaved] = useState([]);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const onFailed = (e) => {
      if (e.detail?.chatRoomId !== chatRoomId) return;
      const msg = e.detail.message;
      setUnsaved((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    };
    const onLanded = (e) => {
      if (e.detail?.chatRoomId !== chatRoomId) return;
      setUnsaved((prev) => prev.filter((m) => m.id !== e.detail.messageId));
    };
    window.addEventListener('guestSaveFailed', onFailed);
    window.addEventListener('guestSaveLanded', onLanded);
    return () => {
      window.removeEventListener('guestSaveFailed', onFailed);
      window.removeEventListener('guestSaveLanded', onLanded);
    };
  }, [chatRoomId]);

  // Try again: sign in again first (put back a vanished sign-in, else a fresh
  // one), then re-send each unsaved write; any that fail again bring the card back
  // Not awaited: offline, a re-send waits for the network like the first one
  // (same id, so whichever lands first wins); a still-waiting one brings the
  // card back after GUEST_SAVE_SLOW_MS.
  const retryUnsaved = async () => {
    const pending = unsaved;
    setRetrying(true);
    logEvent('guest_save_retry', { chatRoomId, count: pending.length });
    await ensureSignedIn();
    setUnsaved([]);
    setRetrying(false);
    pending.forEach((msg) => sendMessage(chatRoomId, msg));
  };

  return { unsaved, retrying, retryUnsaved };
};

export default useGuestSaveRetry;
